import { useState, useRef } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, Typography } from "@mui/material";

import { CxButton } from "../cmx/CxButton";
import { docCreate } from "../gateway/DocService";
import { FileUtil } from "../lib/FileUtil";

// ----------------------------------------------------------------------------------------------------------------
// DocUploadPanel
// ----------------------------------------------------------------------------------------------------------------

const MAX_FILE_SIZE = 6 * 1024 * 1024;          // 6 MB

const SUPPORTED_FILE_TYPES = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "image/png",
    "image/jpeg"
];

export function DocUploadPanel({ open, handleClose, projectId, projectTaskId = null, onUploadSuccess }) {
    const [file, setFile] = useState(null);
    const [label, setLabel] = useState("");
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    //
    // Handler
    //
    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const selected = e.target.files[0];

            // check allowed type
            if (!SUPPORTED_FILE_TYPES.includes(selected.type)) {
                alert("Dieser Dateityp wird nicht unterstützt.");
                return;
            }

            setFile(selected);

            // default label if empty
            if (!label) setLabel(selected.name);
        }
    };

    //
    // Trigger hidden file input via CxButton
    //
    const triggerFileSelect = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    //
    // Upload
    //
    const handleUpload = async () => {
        if (!file) {
            alert("Bitte wählen Sie eine Datei aus.");
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            alert(`Datei ist zu gross (max. ${MAX_FILE_SIZE / (1024*1024)} MB).`);
            return;
        }

        setUploading(true);

        try {
            const base64Content = await FileUtil.fileToBase64(file);
            const values = {
                project_id: projectId,
                project_task_id: projectTaskId,
                label,
                file_name: file.name,
                mime_type: file.type,
                content: base64Content
            };

            const response = await docCreate(values);

            if (response.isSuccess) {
                if (onUploadSuccess) onUploadSuccess(response.result);

                // reset fields after successful upload
                setFile(null);
                setLabel("");

                handleClose();
            } else {
                alert("Upload fehlgeschlagen: " + response.message);
            }
        } catch (err) {
            console.error("Upload error", err);
            alert("Fehler beim Upload: " + err.message);

        } finally {
            setUploading(false);
        }
    };

    const handleCancel = () => {
        handleClose();
        setFile(null);
        setLabel("");
    };

    return (
        <>
        <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
            <DialogTitle>Dokument hochladen</DialogTitle>

            <DialogContent>
                <TextField
                    label="Bezeichnung"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    fullWidth
                    margin="dense"
                />
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    accept={SUPPORTED_FILE_TYPES.join(",")}
                />
                {/* Dateiauswahl Button und Anzeige Dateiname in einer Zeile */}
                <Box sx={{display: "flex", alignItems: "center", gap: 2, mt: 2, ml: -1, px: 1}}>
                    <CxButton
                        kind="icon"
                        label="Auswählen..."
                        iconName="select"
                        onClick={triggerFileSelect}
                        disabled={uploading}
                    />
                    <Typography variant="body2"
                        sx={{
                            color: file ? "text.primary" : "text.disabled",
                            fontStyle: file ? "normal" : "italic",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            flexGrow: 1
                        }}
                        title={file?.name}
                    >
                        {file ? file.name : "Kein Dokument ausgewählt"}
                    </Typography>
                    <input
                        type="file"
                        id="cx-doc-upload-input"
                        accept={SUPPORTED_FILE_TYPES.join(",")}
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <CxButton
                    kind="text"
                    label="Abbrechen"
                    onClick={handleCancel}
                    disabled={uploading}
                />
                <CxButton
                    kind="icon"
                    label={uploading ? "Am laden..." : "Hochladen"}
                    iconName="upload"
                    onClick={handleUpload}
                    disabled={uploading || !file}
                />
            </DialogActions>
        </Dialog>
        </>
    );
}
